import { t } from '@exam-prep/i18n';
import { Card, CardHeader, CardTitle, CardContent, cn } from '@exam-prep/ui';
import { when } from '../../../lib/format';
import type { Result as ResultData, Leaderboard } from '../../../lib/api-types';
import { QuestionFigure } from '../../../components/QuestionFigure';
import { ScoreRing, Bar } from './Result';

/** The score card: ring, right/wrong/skipped tiles, normalised score and rank. */
export function ScoreCard({ data }: { data: ResultData }) {
  const r = data.rank;
  return (
    <Card className="live-card overflow-hidden">
      <CardHeader className="items-center text-center">
        <p className="text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">
          {t('miniapp.result.title')}
        </p>
        <CardTitle>{data.mock.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-center text-sm">
        <ScoreRing score={data.score.total} max={data.score.max} />
        <p className="sr-only">
          {t('miniapp.result.score', { score: data.score.total, max: data.score.max })}
        </p>
        <div className="grid grid-cols-3 gap-2" aria-hidden>
          <span className="stat-tile">
            <b className="font-display text-xl text-ok">{data.score.correct}</b>
            <span className="text-xs text-muted-foreground">{t('miniapp.result.right')}</span>
          </span>
          <span className="stat-tile">
            <b className="font-display text-xl text-danger">{data.score.wrong}</b>
            <span className="text-xs text-muted-foreground">{t('miniapp.result.wrong')}</span>
          </span>
          <span className="stat-tile">
            <b className="font-display text-xl">{data.score.unanswered}</b>
            <span className="text-xs text-muted-foreground">
              {t('miniapp.result.skipped_short')}
            </span>
          </span>
        </div>
        {data.normalisedScore !== null && data.normalisedScore !== data.score.total ? (
          <p>{t('miniapp.result.normalised', { score: data.normalisedScore })}</p>
        ) : null}
        <p className="sr-only">
          {t('miniapp.result.counts', {
            right: data.score.correct,
            wrong: data.score.wrong,
            skipped: data.score.unanswered,
          })}
        </p>
        {r ? (
          <>
            <p className="font-display text-lg font-semibold">
              <span className="brand-accent-text">
                {r.provisional
                  ? t('miniapp.result.rank_provisional', { rank: r.rank, cohort: r.cohort })
                  : t('miniapp.result.rank', { rank: r.rank, cohort: r.cohort })}
              </span>
            </p>
            <p>{t('miniapp.result.percentile', { p: r.percentile })}</p>
            {r.predictedLow !== null && r.predictedHigh !== null ? (
              <p>{t('miniapp.result.predicted', { low: r.predictedLow, high: r.predictedHigh })}</p>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Score per section, with a bar each. */
export function SectionsCard({ data }: { data: ResultData }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('miniapp.result.sections')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {data.score.sections.map((s) => (
          <div key={s.key} className="space-y-1.5">
            <div className="flex justify-between gap-3">
              <span className="font-medium">{s.subjectName ?? s.key}</span>
              <span className="shrink-0 whitespace-nowrap tabular-nums">
                {s.score} / {s.max} ({s.correct}✓ {s.wrong}✗)
              </span>
            </div>
            <Bar value={s.score} max={s.max} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** The top 20 of a ranked mock, with the learner's own row highlighted. */
export function LeaderboardCard({ rows }: { rows: Leaderboard['rows'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('miniapp.result.leaderboard')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {rows.slice(0, 20).map((row) => (
          <div
            key={`${String(row.rank)}-${row.name}`}
            className={cn(
              'flex justify-between rounded-lg px-2 py-1.5 tabular-nums',
              row.me && 'border border-brand-amber bg-accent font-semibold',
            )}
          >
            <span>
              #{row.rank} {row.name}
            </span>
            <span>{row.score ?? '–'}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** Each question with the right answer, the learner's choice and the explanation, once released. */
export function SolutionsCard({ data }: { data: ResultData }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('miniapp.result.solutions')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {data.solutions ? (
          data.solutions.map((s) => (
            <div key={s.key} className="space-y-3">
              <p className="font-medium">{s.subjectName}</p>
              {s.items.map((it, i) => (
                <div key={it.itemId} className="rounded-xl border bg-card p-3">
                  <p className="whitespace-pre-wrap">
                    {i + 1}. {it.stem}
                  </p>
                  {it.figure ? <QuestionFigure figure={it.figure} /> : null}
                  <ul className="my-1">
                    {it.options.map((o, j) => (
                      <li
                        key={j}
                        className={cn(
                          j === it.key && 'font-semibold text-green-700',
                          j === it.choice && j !== it.key && 'text-red-700 line-through',
                        )}
                      >
                        {String.fromCharCode(65 + j)}. {o}
                      </li>
                    ))}
                  </ul>
                  {it.choice === null ? (
                    <p className="text-xs text-muted-foreground">{t('miniapp.result.skipped')}</p>
                  ) : null}
                  {it.explanationDetailed || it.explanationShort ? (
                    <p className="text-xs whitespace-pre-wrap text-muted-foreground">
                      {it.explanationDetailed || it.explanationShort}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          ))
        ) : (
          <p>{t('miniapp.result.solutions_later', { at: when(data.mock.resultsAt) })}</p>
        )}
      </CardContent>
    </Card>
  );
}
